import React, { useEffect, useLayoutEffect, useState } from "react";
import Chart, { loaderTypes } from "../../../../Components/Chart/Chart";
import useChart from "../../../../Components/Chart/useChart";
import useSeverityAsPriority from "../../../../General/useSeverityAsPriority";
import { useDebounce, useMeasure } from "react-use";
import * as am4charts from "@amcharts/amcharts4/charts";
import "./FindingsBySeverity.scss";
import {
  buildChartData,
  createCategoryAxis,
  createSeries,
  createValueAxis,
  type ChartDataPoint,
} from "../WidthSensetiveStackedBarChartUtils";
import useLogger from "../../../../Logger/useLogger";
import type { CountBySeverity } from "../TopFeatureTables/TopFeatureTable";

export interface FindingsBySeverityChartProps {
  findingCounts?: CountBySeverity | null;
  totalCount?: number;
  maxFindings: number;
  namePrefix: string;
  filterFieldValue?: string;
  loading?: boolean;
  error?: string | boolean | null;
  widgetId: string;
  onChartClick?: (severity?: string, filterField?: unknown[]) => void;
  isForExport?: boolean;
  setLoaded?: (isLoaded: boolean) => void;
}

const MAX_DOM_TOKEN_LENGTH = 40;

const toDomToken = (value: string): string => {
  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, MAX_DOM_TOKEN_LENGTH);
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (Math.imul(hash, 31) + value.charCodeAt(i)) | 0;
  }
  return `${slug || "row"}-${(hash >>> 0).toString(36)}`;
};

const FindingsBySeverityChart: React.FC<FindingsBySeverityChartProps> = ({
  findingCounts,
  totalCount = undefined,
  maxFindings,
  namePrefix,
  filterFieldValue,
  loading,
  error,
  widgetId,
  onChartClick,
  isForExport,
  setLoaded,
}) => {
  const { currentSeverityMapping } = useSeverityAsPriority();
  const { logError } = useLogger();

  const [dashboardData, setDashboardData] = useState<ChartDataPoint[] | null>(
    null
  );
  const [ref, { width: chartWidth }] = useMeasure<HTMLDivElement>();
  const [debouncedWidth, setDebouncedWidth] = useState(chartWidth);
  const chartName = `${toDomToken(namePrefix)}-${widgetId}-findings-by-severity`;

  const {
    chartLoading,
    dataExists,
    setChartError,
    setChartDataExists,
    startLoadingChart,
    stopLoadingChart,
    createChart,
  } = useChart(isForExport ? (setLoaded ?? (() => {})) : null);

  useEffect(() => {
    if (!!loading) {
      startLoadingChart();
      setChartError(false);
      setChartDataExists(false);
    }
  }, [loading]);

  useEffect(() => {
    if (!!error) {
      stopLoadingChart();
      setChartError(true);
      setChartDataExists(false);
      logError(error, "FindingsBySeverityChart: failed to load findings data", {
        tags: { feature: "dashboard_widget", widget: "findings_by_severity" },
        extra: { widgetId, namePrefix },
      });
    }
  }, [error]);

  useDebounce(
    () => {
      setDebouncedWidth(chartWidth);
    },
    1000,
    [chartWidth]
  );

  useEffect(() => {
    if (!findingCounts) {
      setDashboardData(null);
      setChartDataExists(false);
      return;
    }
    if (!!debouncedWidth) {
      const findingsTotal = totalCount ?? findingCounts.total;
      const data = buildChartData(
        currentSeverityMapping?.map((s) => s.name) ?? [],
        currentSeverityMapping?.map((s) => findingCounts[s.value]) ?? [],
        findingsTotal,
        maxFindings,
        debouncedWidth,
        { source: namePrefix, filterFieldValue: filterFieldValue ?? "" }
      );

      setDashboardData([data]);
      setChartError(false);
      stopLoadingChart();
      setChartDataExists(true);
    }
  }, [findingCounts, totalCount, debouncedWidth]);

  const getChart = (
    dashboardData: ChartDataPoint[]
  ): am4charts.XYChart | undefined => {
    if (dataExists) {
      let [chart] = createChart(chartName, am4charts.XYChart, {
        hideLegend: true,
      });
      chart.data = dashboardData;
      chart.paddingTop = 0;
      chart.paddingBottom = 0;

      let categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
      createCategoryAxis(categoryAxis, "source");
      let valueAxis = chart.xAxes.push(new am4charts.ValueAxis());
      createValueAxis(valueAxis, maxFindings);

      currentSeverityMapping?.forEach((severity) => {
        const { name, value, color } = severity;
        if (!findingCounts?.[value]) {
          return;
        }
        const series = createSeries(
          `${name}Val`,
          name,
          findingCounts[value],
          color,
          "source",
          !!isForExport,
          !!onChartClick
        );

        series.columns.template.events.on("hit", (e) => {
          if (!!onChartClick) {
            const clickedSeverity = currentSeverityMapping
              ?.find((s) => s.name === name)
              ?.value?.toString();
            const dataContext = e.target.dataItem?.dataContext as
              | ChartDataPoint
              | undefined;
            const clickedFilterField = [dataContext?.filterFieldValue];

            onChartClick(clickedSeverity, clickedFilterField);
          }
        });

        chart.series.push(series);
      });

      return chart;
    }
  };

  useLayoutEffect(() => {
    if (!!dashboardData && !!findingCounts) {
      const chart = getChart(dashboardData);

      return () => {
        chart?.dispose();
      };
    }
  }, [dashboardData, createChart, isForExport]);

  return (
    <Chart
      chartLoading={chartLoading}
      chartName={chartName}
      chartError={error ?? null}
      chartData={dataExists}
      hideLegend
      loaderType={loaderTypes.SKELETON}
      // useMeasure returns a callback ref; Chart types chartContainerRef as a
      // RefObject. Behavior is unchanged — cast bridges the ref-shape mismatch.
      chartContainerRef={ref as unknown as React.RefObject<HTMLDivElement>}
    />
  );
};

export default FindingsBySeverityChart;
